import { useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { NextFilterAndSortListings } from "@/pages/root/shop/NextFilterAndSortListings";
import { convertProductTypeToListingType } from "@/utils/productTypes";
import { get } from "api";
import { AffirmBanner } from "atoms/affirm/affirmBanner/AffirmBanner";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import useDunningMembership from "hooks/useDunningMemberships";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisCTAVariation2 from "modules/adonis/AdonisCTAVariation2/AdonisCTAVariation2";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { AffirmModal } from "molecules/affirm-modal/AffirmModal";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import { queryClient } from "pages/Root";
import { FilterAndSortListings } from "pages/root/shop/FilterAndSortListings";
import { useFeatureFlag } from "services/Flags";
import {
  getListingsWithDiscountInformation,
  hasLinearPricing,
} from "services/ListingHelperService";
import { notNull } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const loader = async () => {
  const shopData = queryClient.fetchQuery(["/shop"], () => get("/shop"));
  const listings = queryClient.fetchQuery(
    ["/listings"],
    () => get("/listings"),
    { staleTime: 1000 * 60 }
  );
  const listingMembershipPrices = queryClient.fetchQuery(
    ["/listings/membership-prices"],
    () => get("/listings/membership-prices")
  );
  const theme = queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );
  const categories = queryClient.fetchQuery(["shop/categories"], () =>
    get("/shop/categories")
  );
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );
  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );
  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );
  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });
  const recommendationIds = queryClient.fetchQuery(
    ["/adonis/top-recommendations/id"],
    () => get("/adonis/top-recommendations/id", { limit: 10 }),
    { staleTime: 1000 * 30 }
  );

  return waitObj({
    platform,
    shopData,
    listings,
    listingMembershipPrices,
    categories,
    theme,
    shopDiscounts,
    customShopCategories,
    user,
    recommendationIds,
  });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;
export const shopTreatmentsLoader: LoaderFunction = () => {
  return loader();
};

const ShopTreatmentsPage = () => {
  const {
    platform,
    shopData,
    listings,
    listingMembershipPrices,
    categories,
    theme,
    shopDiscounts,
    customShopCategories,
    user,
    recommendationIds,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const { openChat } = useAdonisChat();
  const [showAffirmModal, setShowAffirmModal] = useState(false);
  const headerImage = theme.resources?.find(
    (resource) => resource.key === "individual-treatments-header"
  )?.url;

  let treatments = listings.filter(({ type }) => type === "Treatment");
  treatments = getListingsWithDiscountInformation(
    treatments,
    customShopCategories,
    shopDiscounts?.listingDiscounts
  );

  const recommendedIds = new Set(recommendationIds);
  const allAreaIds = new Set(treatments.flatMap(({ areaIds }) => areaIds));
  const allConcernIds = new Set(
    treatments.flatMap(({ concernIds }) => concernIds)
  );

  const filteredConcerns =
    shopData.concerns?.filter(({ id }) => allConcernIds.has(id)) ?? [];

  const filteredAreas =
    shopData.areas?.filter(({ id }) => allAreaIds.has(id)) ?? [];

  const treatmentsCategory = categories.find(
    ({ defaultCategoryId }) => defaultCategoryId === "treatments"
  );
  const { dunningMembership } = useDunningMembership();

  const { isEnabled: shouldShowAdonisCTA } = useAdonisChat();
  const adonisShopCTAText = useFeatureFlag("adonisShopCTAText");
  const isAlgoliaFilteringEnabled = useFeatureFlag("algoliaFiltering");
  const handleAdonisCTAClick = () => {
    openChat();
    trackAdonisViewed();
  };

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  return (
    <div className="flex flex-col bg-two">
      {platform.platformSettings?.affirmEnabled && (
        <AffirmModal
          platformLogo={platform.logoImageUrl ?? ""}
          open={showAffirmModal}
          onClose={() => setShowAffirmModal(false)}
          hideRewardText={!platform?.platformSettings?.rewardsEnabled}
        />
      )}
      {treatmentsCategory && (
        <ShopListHeader
          title={treatmentsCategory.title}
          subtitle={treatmentsCategory.description}
          bgGradient
          // TODO(CAA-158) using the header image from theme until we implement CAA-158
          backgroundImageUrl={headerImage}
        />
      )}
      {!!listings.length && (
        <>
          {isAlgoliaFilteringEnabled ? (
            <NextFilterAndSortListings
              types={["Treatment"]}
              areas={shopData.areas ?? []}
              concerns={shopData.concerns ?? []}
              platformId={platform.id}
              platformLocationId={user.platformLocation?.id}
            >
              {(hit, sendEvent, index) => {
                const [enhancedHit] = getListingsWithDiscountInformation(
                  [{ ...hit, id: hit.objectID }],
                  customShopCategories,
                  shopDiscounts?.listingDiscounts
                );

                const handleClick = () => {
                  sendEvent("click", hit, "Hit Clicked");
                  navigate(`/pdp/package/${enhancedHit.objectID}`);
                };

                return (
                  <>
                    <ProductCardLarge
                      key={enhancedHit.objectID}
                      itemId={enhancedHit.objectID}
                      title={enhancedHit.title}
                      description={enhancedHit.description}
                      imageUrl={enhancedHit.imageUrl}
                      type={convertProductTypeToListingType(
                        enhancedHit.productType
                      )}
                      tag=""
                      concerns={enhancedHit.concernNames}
                      price={enhancedHit.minBasePrice}
                      memberPrice={
                        listingMembershipPrices[enhancedHit.objectID]
                      }
                      onClick={handleClick}
                      unitType={enhancedHit.unitName}
                      hasLinearPricing={enhancedHit.hasLinearPricing}
                      discount={enhancedHit.discountText}
                      customColor={enhancedHit.customColor}
                      guestMode={user.isGuest}
                      isRecommended={recommendedIds.has(enhancedHit.objectID)}
                      bookingEnabled={
                        platform.platformSettings?.partnerEmrBookingEnabled
                      }
                    />
                    {shouldShowAdonisCTA && index === 0 && (
                      <AdonisCTAVariation2
                        onClick={handleAdonisCTAClick}
                        ctaText={adonisShopCTAText}
                      />
                    )}
                    {dunningMembership.status === false &&
                      platform.platformSettings?.affirmEnabled &&
                      (treatments.length < 2 || index === 1) && (
                        <AffirmBanner
                          onClick={() => setShowAffirmModal(true)}
                          hasColorGradient
                          hasBorderRadius
                          hasShadow
                          hideRewardText={
                            !platform?.platformSettings?.rewardsEnabled
                          }
                        />
                      )}
                  </>
                );
              }}
            </NextFilterAndSortListings>
          ) : (
            <FilterAndSortListings
              types={["Treatment"]}
              areas={filteredAreas}
              concerns={filteredConcerns}
              listings={treatments}
            >
              {(listing, index) => (
                <>
                  <ProductCardLarge
                    key={listing.id}
                    itemId={listing.id}
                    title={listing.title}
                    description={listing.description}
                    imageUrl={listing.imageUrl}
                    type={listing.type}
                    tag=""
                    concerns={filteredConcerns
                      .filter(({ id }) => listing.concernIds.includes(id))
                      .map(({ name }) => name)
                      .filter(notNull)}
                    price={listing.baseCost}
                    memberPrice={listing.membershipCost}
                    onClick={() => {
                      navigate(`/pdp/package/${listing.id}`);
                    }}
                    unitType={listing.packageUnitType?.singularUnitType}
                    hasLinearPricing={hasLinearPricing(
                      listing.packages?.flatMap(
                        ({ packagePrices }) => packagePrices
                      )
                    )}
                    discount={listing.discountText}
                    customColor={listing.customColor}
                    guestMode={user.isGuest}
                    isRecommended={recommendedIds.has(listing.id)}
                    bookingEnabled={
                      platform.platformSettings?.partnerEmrBookingEnabled
                    }
                  />
                  {shouldShowAdonisCTA && index === 0 && (
                    <AdonisCTAVariation2
                      onClick={handleAdonisCTAClick}
                      ctaText={adonisShopCTAText}
                    />
                  )}
                  {dunningMembership.status === false &&
                    platform.platformSettings?.affirmEnabled &&
                    (treatments.length < 2 || index === 1) && (
                      <AffirmBanner
                        onClick={() => setShowAffirmModal(true)}
                        hasColorGradient
                        hasBorderRadius
                        hasShadow
                        hideRewardText={
                          !platform?.platformSettings?.rewardsEnabled
                        }
                      />
                    )}
                </>
              )}
            </FilterAndSortListings>
          )}
        </>
      )}
      <RepeatMDFooter />
    </div>
  );
};

export default ShopTreatmentsPage;
